import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { StorageKey } from './model/StorageKey';

export class CookieStorage {
    set(key: StorageKey, value: string, options?: Cookies.CookieAttributes): void {
        Cookies.set(key, value, { secure: true, ...options });
    }

    get(key: StorageKey): string | null {
        return Cookies.get(key) ?? null;
    }

    delete(key: StorageKey): void {
        Cookies.remove(key);
    }
}

export const useCookieStorage = (key: StorageKey): UseQueryResult<string | null> => {
    const storage = new CookieStorage();

    return useQuery({
        queryFn: (): string | null => storage.get(key),
        queryKey: [key],
        // staleTime and cacheTime set to 0 to always read fresh cookie values.
        // Cookies can be set externally (e.g., from SFCC controllers) and React Query
        // won't detect those changes. This ensures we always get the current cookie value.
        // Note: cacheTime is renamed to gcTime in React Query v5.
        staleTime: 0,
        cacheTime: 0,
    });
};

export const useCookieStorageMutation = (
    key: StorageKey,
): UseMutationResult<unknown, unknown, string> => {
    const queryClient = useQueryClient();
    const storage = new CookieStorage();

    return useMutation<unknown, unknown, string>({
        mutationFn: async (value: string) => {
            storage.set(key, value);
            return Promise.resolve();
        },
        onSuccess: async () => {
            await Promise.all([queryClient.invalidateQueries([key])]);
        },
    });
};
